import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Log or edit a pre-screening call against a ticket (Feature 2 / §22.1).
 *
 * The "call the customer before you dispatch" step: an agent phones the
 * requester / on-site contact to confirm scope, access and timing, and records
 * the outcome + notes.
 *
 * Two modes:
 *   - Create mode (default, `existingCall` not provided): POSTs to
 *     /tickets/:id/prescreen-calls and shows a "Log pre-screening call" button
 *     that toggles the form open. Invalidates activity + the calls list on
 *     success so both surfaces refresh.
 *   - Edit mode (`existingCall` provided): PATCHes the call's row, pre-filled
 *     with its current values. Edit window is enforced by the BACKEND (5 min
 *     after creation) — the frontend gates the button render too.
 *
 * Gated by the `ticket.comment.internal` permission (matches the backend
 * require_permission).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
const OUTCOMES = [
    { value: 'reached', label: 'Customer reached' },
    { value: 'no_answer', label: 'No answer' },
    { value: 'voicemail', label: 'Voicemail left' },
    { value: 'callback_scheduled', label: 'Callback scheduled' },
    { value: 'declined', label: 'Declined' },
    { value: 'other', label: 'Other' },
];
export function PrescreeningCallForm({ ticketId, existingCall, alwaysOpen, onDone }) {
    const toast = useToast();
    const qc = useQueryClient();
    const isEdit = !!existingCall;
    const [open, setOpen] = useState(!!alwaysOpen || isEdit);
    const [outcome, setOutcome] = useState(existingCall?.outcome ?? 'reached');
    const [notes, setNotes] = useState(existingCall?.notes ?? '');
    const [contactName, setContactName] = useState(existingCall?.contact_name ?? '');
    const [contactPhone, setContactPhone] = useState(existingCall?.contact_phone ?? '');
    const [duration, setDuration] = useState(existingCall?.duration_minutes != null ? String(existingCall.duration_minutes) : '');
    // Re-sync local state when the underlying call changes (e.g., parent reused
    // the same form instance for a different row).
    useEffect(() => {
        if (existingCall) {
            setOutcome(existingCall.outcome);
            setNotes(existingCall.notes ?? '');
            setContactName(existingCall.contact_name ?? '');
            setContactPhone(existingCall.contact_phone ?? '');
            setDuration(existingCall.duration_minutes != null ? String(existingCall.duration_minutes) : '');
        }
    }, [existingCall]);
    const reset = () => {
        if (!isEdit) {
            setOutcome('reached');
            setNotes('');
            setContactName('');
            setContactPhone('');
            setDuration('');
        }
        if (alwaysOpen || isEdit) {
            onDone?.();
        }
        else {
            setOpen(false);
        }
    };
    const save = useMutation({
        mutationFn: () => {
            const body = {
                outcome,
                notes: notes.trim() || null,
                contact_name: contactName.trim() || null,
                contact_phone: contactPhone.trim() || null,
                duration_minutes: duration.trim() ? Number(duration) : null,
            };
            if (isEdit && existingCall) {
                return api.patch(`/tickets/${ticketId}/prescreen-calls/${existingCall.id}`, body);
            }
            return api.post(`/tickets/${ticketId}/prescreen-calls`, body);
        },
        onSuccess: () => {
            toast.success(isEdit ? 'Pre-screening call updated' : 'Pre-screening call logged');
            // Both surfaces refresh: the dedicated card + the unified activity feed.
            qc.invalidateQueries({ queryKey: ['ticket', ticketId, 'activity'] });
            qc.invalidateQueries({ queryKey: ['ticket', ticketId, 'prescreen-calls'] });
            reset();
        },
        onError: (e) => toast.error(isEdit ? 'Could not update the call' : 'Could not log the call', e.message),
    });
    if (!open && !alwaysOpen && !isEdit) {
        return (_jsx("div", { className: "mb-4", children: _jsx(Button, { variant: "outline", size: "sm", onClick: () => setOpen(true), children: "\uD83D\uDCDE Log pre-screening call" }) }));
    }
    return (_jsxs("div", { className: "mb-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/40", children: [_jsx("div", { className: "text-sm font-semibold text-slate-700 dark:text-slate-200", children: isEdit ? 'Edit pre-screening call' : 'Log pre-screening call' }), _jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [_jsxs("label", { className: "block text-xs font-medium text-slate-600 dark:text-slate-300", children: ["Outcome", _jsx(Select, { className: "mt-1", value: outcome, onChange: (e) => setOutcome(e.target.value), children: OUTCOMES.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value))) })] }), _jsxs("label", { className: "block text-xs font-medium text-slate-600 dark:text-slate-300", children: ["Duration (minutes)", _jsx(Input, { className: "mt-1", type: "number", min: 0, max: 1440, inputMode: "numeric", placeholder: "e.g. 5", value: duration, onChange: (e) => setDuration(e.target.value.replace(/\D/g, '').slice(0, 4)) })] }), _jsxs("label", { className: "block text-xs font-medium text-slate-600 dark:text-slate-300", children: ["Who you called", _jsx(Input, { className: "mt-1", placeholder: "Contact name (optional)", value: contactName, onChange: (e) => setContactName(e.target.value), maxLength: 200 })] }), _jsxs("label", { className: "block text-xs font-medium text-slate-600 dark:text-slate-300", children: ["Phone", _jsx(Input, { className: "mt-1", placeholder: "Number called (optional)", value: contactPhone, onChange: (e) => setContactPhone(e.target.value), maxLength: 64 })] })] }), _jsxs("label", { className: "block text-xs font-medium text-slate-600 dark:text-slate-300", children: ["Notes", _jsx(Textarea, { className: "mt-1", rows: 3, placeholder: "What was discussed / confirmed on the call", value: notes, onChange: (e) => setNotes(e.target.value), maxLength: 4000 })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { size: "sm", disabled: save.isPending, onClick: () => save.mutate(), children: save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save call' }), _jsx(Button, { size: "sm", variant: "ghost", disabled: save.isPending, onClick: reset, children: "Cancel" })] })] }));
}
